import type { GuidanceEventInput, GuidancePersona, GuidanceViewport } from '@bookora/shared';

/** An event as a panel component describes it; ids, time and context are filled in. */
export type ClientGuidanceEvent = Omit<GuidanceEventInput, 'clientEventId' | 'occurredAt'>;

/** An event waiting in the queue, stamped with the clinic it was built under. */
export interface QueuedEvent extends GuidanceEventInput {
  clinicId: string;
}

/**
 * A v4 UUID. `crypto.randomUUID` exists only in secure contexts, and a
 * reception tablet reaching a LAN build over `http://192.168…` is not one.
 */
export function newClientEventId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === 'function') {
    try {
      return c.randomUUID();
    } catch {
      // Non-secure context: fall through.
    }
  }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function buildEvent(
  ev: ClientGuidanceEvent,
  ctx: { clinicId: string; viewport: GuidanceViewport; persona: GuidancePersona },
  now: Date = new Date(),
): QueuedEvent {
  return {
    viewport: ctx.viewport,
    persona: ctx.persona,
    ...ev,
    clientEventId: newClientEventId(),
    occurredAt: now.toISOString(),
    clinicId: ctx.clinicId,
  };
}

/** The wire shape: only the fields `GuidanceEventDto` declares (the server rejects the rest). */
export function toWire(ev: QueuedEvent): Record<string, unknown> {
  const out: Record<string, unknown> = {
    clientEventId: ev.clientEventId,
    type: ev.type,
    kind: ev.kind,
    key: ev.key,
    version: ev.version,
    occurredAt: ev.occurredAt,
  };
  const optional = [
    'instanceKey',
    'step',
    'stepId',
    'total',
    'source',
    'surface',
    'rank',
    'viewport',
    'persona',
    'durationMs',
    'snoozeDays',
    'reason',
  ] as const;
  for (const field of optional) {
    const value = ev[field];
    if (value !== undefined && value !== null) out[field] = value;
  }
  return out;
}
